/**
 * Editor de landing page — moldura e composição.
 *
 * Rota: /landing/:idOuSlug — funciona por slug (dá para mandar a URL no chat,
 * F5 não perde o lugar) e por id. Todo o estado mora em `useEditorPagina`; a
 * paleta/estrutura em `PainelBlocos`; a prévia e o inspetor são os componentes
 * que o editor antigo já usava. Este arquivo só liga as peças.
 *
 * O layout é o argumento: paleta à esquerda, a página REAL ao centro (renderizada
 * pela mesma função da URL pública), inspetor à direita. O centro não é um
 * desenho da página — é a página.
 */

import { SEGMENTOS, type ConfigMedicao } from '@egba-do/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { api, type Categoria, type Configuracao, type Item, type Lista } from '../../api/cliente'
import { InspetorBloco } from '../../componentes/landing/InspetorBloco'
import { LARGURAS, Preview, type Dispositivo } from '../../componentes/landing/Preview'
import { Aviso, Botao, Carregando, Modal, Vazio } from '../../componentes/ui'
import { ConfigPagina } from './ConfigPagina'
import { PainelBlocos } from './PainelBlocos'
import { useEditorPagina } from './useEditorPagina'

const DISPOSITIVOS: Array<{ id: Dispositivo; rotulo: string; icone: string }> = [
  { id: 'desktop', rotulo: 'Computador', icone: 'M3 5h18v11H3zM8 20h8M12 16v4' },
  { id: 'tablet', rotulo: 'Tablet', icone: 'M6 3h12v18H6zM11 18h2' },
  { id: 'celular', rotulo: 'Celular', icone: 'M8 2h8v20H8zM11 18.5h2' },
]

export function EditorLanding() {
  const { idOuSlug } = useParams<{ idOuSlug: string }>()
  const navegar = useNavigate()

  const e = useEditorPagina(idOuSlug!)
  const [dispositivo, setDispositivo] = useState<Dispositivo>('desktop')
  const [abaConfig, setAbaConfig] = useState(false)
  const [publicando, setPublicando] = useState(false)

  const { data: categorias } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => api.get<Lista<Categoria>>('/api/v1/admin/categorias'),
  })

  const { data: campanhas } = useQuery({
    queryKey: ['campanhas'],
    queryFn: () => api.get<Lista<{ id: string; nome: string }>>('/api/v1/campanhas'),
  })

  // A medição da conta é só informativa aqui — serve para o operador não
  // recadastrar o mesmo pixel na página. Quem não tem acesso a configurações
  // simplesmente não recebe a lista, e a tela segue funcionando.
  const { data: configs } = useQuery({
    queryKey: ['configuracoes'],
    queryFn: () => api.get<Lista<Configuracao>>('/api/v1/admin/configuracoes'),
    retry: false,
  })
  const medicao = (configs?.dados.find((c) => c.chave === 'marketing.medicao')?.valor ??
    null) as ConfigMedicao | null

  const reescrever = useMutation({
    mutationFn: (texto: string) =>
      api.post<Item<{ texto: string }>>('/api/v1/estudio/copy', { texto, instrucao: 'clarear' }),
  })

  function sair(): void {
    if (e.sujo && !window.confirm('Há alterações não salvas nesta página. Sair mesmo assim?')) {
      return
    }
    void navegar('/landing')
  }

  if (e.carregando) return <Carregando />

  if (!e.pagina || !e.paraRender) {
    return (
      <Vazio
        titulo="Landing page não encontrada"
        descricao={e.falha ?? 'Ela pode ter sido removida, ou o endereço mudou.'}
        acao={
          <Botao onClick={() => void navegar('/landing')}>Ver as landing pages</Botao>
        }
      />
    )
  }

  const pagina = e.pagina
  const bloco = pagina.blocos.find((b) => b.id === e.selecionado) ?? null

  return (
    // z-50 e não z-30: o menu lateral do painel é z-40 e tem exatamente
    // 248px — a mesma largura da paleta de blocos —, então ficava por cima
    // dela. A paleta existia, desenhada, e simplesmente não dava para ver.
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* ------------------------------------------------------------- topo */}
      <header className="flex h-[60px] shrink-0 items-center gap-4 border-b border-[color:var(--color-borda)] px-4">
        <button
          type="button"
          onClick={sair}
          className="rounded-lg px-2 py-1 text-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
        >
          ← Landing pages
        </button>

        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold text-slate-900">{pagina.titulo}</p>
          <p className="truncate text-xs text-slate-400">
            /lp/{pagina.slug}
            {e.sujo && <span className="ml-2 text-amber-600">• não salvo</span>}
          </p>
        </div>

        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
          {DISPOSITIVOS.map((d) => (
            <button
              key={d.id}
              type="button"
              title={`${d.rotulo} · ${LARGURAS[d.id]}px`}
              aria-pressed={dispositivo === d.id}
              onClick={() => setDispositivo(d.id)}
              className={`flex size-8 items-center justify-center rounded-md transition ${
                dispositivo === d.id
                  ? 'bg-white text-marca-700 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-[17px]"
                aria-hidden="true"
              >
                <path d={d.icone} />
              </svg>
            </button>
          ))}
        </div>

        <button
          type="button"
          title="Desfazer (Ctrl+Z)"
          disabled={!e.temHistorico}
          onClick={e.desfazer}
          className="rounded-lg px-2 py-1 text-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:opacity-30 disabled:hover:bg-transparent"
        >
          ↶ Desfazer
        </button>

        <Botao variante="secundario" tamanho="sm" onClick={() => setAbaConfig(true)}>
          Configurações
        </Botao>
        <Botao
          variante="secundario"
          tamanho="sm"
          disabled={!e.sujo}
          carregando={e.salvar.isPending}
          onClick={() => e.salvar.mutate()}
        >
          {e.sujo ? 'Salvar' : 'Salvo'}
        </Botao>
        <Botao
          tamanho="sm"
          disabled={e.problemas.length > 0 || e.sujo}
          title={
            e.problemas.length > 0
              ? 'Resolva as pendências antes de publicar'
              : e.sujo
                ? 'Salve o rascunho antes de publicar'
                : undefined
          }
          onClick={() => setPublicando(true)}
        >
          {pagina.publicada ? 'Republicar' : 'Publicar'}
        </Botao>
      </header>

      {(e.erro || e.aviso || e.problemasGerais.length > 0) && (
        <div className="shrink-0 space-y-2 border-b border-[color:var(--color-borda)] px-4 py-2">
          {e.erro && <Aviso tom="perigo">{e.erro}</Aviso>}
          {e.aviso && !e.erro && <Aviso tom="sucesso">{e.aviso}</Aviso>}
          {e.problemasGerais.length > 0 && (
            <Aviso tom="atencao" titulo="Falta resolver antes de publicar">
              <ul className="list-inside list-disc">
                {e.problemasGerais.map((p, i) => (
                  <li key={`${i}-${p.mensagem}`}>{p.mensagem}</li>
                ))}
              </ul>
            </Aviso>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------ corpo */}
      <div className="grid min-h-0 flex-1 grid-cols-[248px_1fr_340px]">
        <PainelBlocos
          blocos={pagina.blocos}
          selecionado={e.selecionado}
          problemasPorBloco={e.problemasPorBloco}
          onSelecionar={e.setSelecionado}
          onInserir={e.inserirBloco}
          onMover={e.moverBloco}
          onDuplicar={e.duplicarBloco}
          onAlternarOculto={e.alternarOculto}
          onRemover={e.removerBloco}
        />

        <main className="min-h-0 overflow-hidden">
          <Preview
            pagina={e.paraRender}
            dispositivo={dispositivo}
            selecionado={e.selecionado}
            onSelecionar={e.setSelecionado}
          />
        </main>

        <aside className="min-h-0 overflow-y-auto border-l border-[color:var(--color-borda)]">
          <InspetorBloco
            bloco={bloco}
            campanhas={campanhas?.dados ?? []}
            categorias={categorias?.dados ?? []}
            segmentos={SEGMENTOS.map((s) => ({ id: s.id, nome: s.nome }))}
            problemas={bloco ? (e.problemasPorBloco.get(bloco.id) ?? []) : []}
            gerandoCopy={reescrever.isPending}
            onGerarCopy={(texto, aplicar) =>
              reescrever.mutate(texto, {
                onSuccess: (r) => aplicar(r.dados.texto),
                onError: (err: Error) => e.setErro(err.message),
              })
            }
            onAlterar={(novo) =>
              e.alterar({ blocos: pagina.blocos.map((b) => (b.id === novo.id ? novo : b)) })
            }
            onDuplicar={() => bloco && e.duplicarBloco(bloco.id)}
            onRemover={() => bloco && e.removerBloco(bloco.id)}
          />
        </aside>
      </div>

      {/* --------------------------------------------------- configurações */}
      <Modal
        aberto={abaConfig}
        aoFechar={() => setAbaConfig(false)}
        titulo="Configurações da página"
      >
        <ConfigPagina
          pagina={pagina}
          paraRender={e.paraRender}
          categorias={categorias?.dados ?? []}
          medicao={medicao}
          onAlterar={e.alterar}
        />
        <div className="mt-5 flex justify-end">
          <Botao onClick={() => setAbaConfig(false)}>Fechar</Botao>
        </div>
      </Modal>

      {/* --------------------------------------------------------- publicar */}
      <Modal aberto={publicando} aoFechar={() => setPublicando(false)} titulo="Publicar página">
        <p className="text-sm text-slate-600">
          A página fica acessível em <strong>/lp/{pagina.slug}</strong>. A publicação passa pelo
          gate eleitoral no servidor: peça institucional ou promocional é recusada durante a janela
          vedada.
        </p>
        <div className="mt-5 flex justify-end gap-3">
          <Botao variante="secundario" onClick={() => setPublicando(false)}>
            Cancelar
          </Botao>
          <Botao
            carregando={e.publicar.isPending}
            onClick={() => e.publicar.mutate(undefined, { onSettled: () => setPublicando(false) })}
          >
            Publicar
          </Botao>
        </div>
      </Modal>
    </div>
  )
}
